import { Timestamp, FieldValue } from "firebase/firestore";

export const formatDate = (timestamp: Timestamp | FieldValue | Date | string | any, showYear: boolean = false): string => {
  let date: Date;

  if (!timestamp) {
    return 'Invalid Date';
  }

  if (timestamp instanceof Date) {
    date = timestamp;
  } else if (timestamp instanceof Timestamp) {
    date = timestamp.toDate();
  } else if (typeof timestamp === 'object' && 'toDate' in timestamp) {
    date = (timestamp as any).toDate();
  } else if (typeof timestamp === 'object' && 'seconds' in timestamp) {
    // Handle plain Firestore timestamp objects with seconds and nanoseconds
    const seconds = timestamp.seconds || 0;
    const nanoseconds = timestamp.nanoseconds || 0;
    date = new Date(seconds * 1000 + nanoseconds / 1000000);
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const numericTimestamp = parseInt(timestamp.toString(), 10);
    // If it's a reasonable timestamp in seconds (after year 2000), convert to milliseconds
    if (numericTimestamp > 946684800) { // Jan 1, 2000 in seconds
      date = new Date(numericTimestamp * 1000);
    } else {
      date = new Date(numericTimestamp);
    }
  } else {
    console.error('Invalid timestamp type:', typeof timestamp, timestamp);
    return 'Invalid Date';
  }

  // Validate the resulting date
  if (isNaN(date.getTime())) {
    console.error('Invalid date created from timestamp:', timestamp);
    return 'Invalid Date';
  }

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
  const year = date.getFullYear().toString().slice(-2);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const finalDate = `${day}/${month}${showYear ? `/${year}` : ''}`;

  return `${finalDate} - ${hours}:${minutes}`;
};

export const extractTimestampSeconds = (timestamp: Timestamp | FieldValue): number => {
  if (timestamp instanceof Timestamp) {
    return timestamp.seconds;
  }
  throw new Error('Invalid timestamp type');
};

export const getDateFromTimestamp = (timestamp: Timestamp | Date | FieldValue): Date => {
  if (timestamp instanceof Date) {
    return timestamp;
  } else if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  } else if (typeof timestamp === 'object' && 'toDate' in timestamp) {
    return (timestamp as any).toDate();
  } else {
    throw new Error('Invalid timestamp type');
  }
};

export const isWithinTimeframe = (timestamp: Timestamp | Date | FieldValue, timeframeInSeconds: number): boolean => {
  const date = getDateFromTimestamp(timestamp);
  return (Date.now() - date.getTime()) / 1000 <= timeframeInSeconds;
};

export const timeAgo = (timestamp: any) => {
  if (!timestamp) return 'Invalid date';

  let milliseconds: number;

  // Handle different timestamp formats
  if (typeof timestamp === 'object' && timestamp.seconds) {
    // Firebase Timestamp with seconds and nanoseconds
    const seconds = timestamp.seconds || 0;
    const nanoseconds = timestamp.nanoseconds || 0;
    milliseconds = seconds * 1000 + nanoseconds / 1000000;
  } else if (timestamp instanceof Date) {
    milliseconds = timestamp.getTime();
  } else if (typeof timestamp === 'object' && 'toDate' in timestamp) {
    milliseconds = (timestamp as any).toDate().getTime();
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const numericTimestamp = parseInt(timestamp.toString(), 10);
    // If it's a reasonable timestamp in seconds (after year 2000), convert to milliseconds
    if (numericTimestamp > 946684800) { // Jan 1, 2000 in seconds
      milliseconds = numericTimestamp * 1000;
    } else {
      milliseconds = numericTimestamp;
    }
  } else {
    console.error('Invalid timestamp format in timeAgo:', timestamp);
    return 'Invalid date';
  }

  const now: any = new Date();
  const lastUpdate: any = new Date(milliseconds);

  // Ensure that the timestamp is valid
  if (isNaN(lastUpdate.getTime())) {
    console.error('Invalid date created in timeAgo:', timestamp);
    return 'Invalid date';
  }

  const diffInMs = now - lastUpdate;
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays > 0) {
    return `${diffInDays} days ago`;
  } else if (diffInHours > 0) {
    return `${diffInHours} hours ago`;
  } else {
    return 'Just now';
  }
};

